/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SupplierMarkets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Autocomplete, TextField, Checkbox } from "@mui/material";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import Stack from "@mui/material/Stack";
import { useDispatch, useSelector } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../redux/actions";
import DialogContentText from "@mui/material/DialogContentText";
import moment from "moment";
import IconButton from "@mui/material/IconButton";
import deleteIcon from "../../assets/delete_icon.png";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ManufacturerDetails from "./ManufacturerDetails";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import * as XLSX from "xlsx";
import profileActions from "../../redux/actions/profile";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import v2AdminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import PropTypes from "prop-types";

const supplierRowShape = PropTypes.shape({
  name: PropTypes.string,
  address: PropTypes.string,
  country: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  supplier_name: PropTypes.string,
  supplier_address: PropTypes.string,
  supplier_country: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
});

function SupplierMarkets({ open, handleClick }) {
  const timeZoneDetails = useSelector((state) => state.profileReducer);
  // Using API search results; local query not used
  const [page, setPage] = useState(1); // Changed to 1-based pagination like VehicleSystems
  const [rowsPerPage] = React.useState(25);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [openAddMftDetails, setOpenAddMftDetails] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [openDeleteMftDetails, setOpenDeleteMftDetails] = React.useState(false);
  const [openEditSupplierDetails, setOpenEditSupplierDetails] =
    React.useState(false);
  const [manufacturerCount, setManufacturerCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // Add state to track active tab
  const [manufacturerExportData, setManufacturerExportData] = useState([]);
  const [totalSupplierCount, setTotalSupplierCount] = useState(0);
  const hasMountedRef = useRef(false);
  const lastSupplierRequestKeyRef = useRef("");

  //Function to Click Row for Delete
  const handleTableRowClick = useCallback((params) => {
    const reqObj = {
      current_time: moment().format(),
      name: params?.row?.name,
      address: params?.row?.address,
      country: params?.row?.country,
    };
    setSelectedRow(reqObj);
    setOpenDeleteMftDetails(true);
  }, []);

  // Edit click is currently disabled in UI; no handler needed

  const COLUMNS = useMemo(() => {
    // Check if the first item in the data has created_at and updated_at fields
    const hasTimestamps =
      tableRowsData.length > 0 &&
      tableRowsData[0]?.created_at &&
      tableRowsData[0]?.updated_at;

    const baseColumns = [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "supplier_name",
        headerName: "Supplier Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "supplier_address",
        headerName: "Address",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "supplier_country",
        headerName: "Country",
        flex: 1,
        minWidth: 110,
        renderCell: (params) => {
          const countries = params.row?.country;

          // Create options list from countries array
          const countryOptionsList = Array.isArray(countries)
            ? countries?.map((country) => ({
                label: country,
                value: country,
              }))
            : [];

          return (
            <CustomSelectV2
              label=""
              required={false}
              optionsList={countryOptionsList}
              selectedValue={
                Array.isArray(countries) ? countries[0] : "No data"
              }
            />
          );
        },
      },
    ];

    // Conditionally add timestamp columns
    const timestampColumns = hasTimestamps
      ? [
          {
            field: "created_at",
            headerName: "Created",
            flex: 0.6,
            minWidth: 100,
          },
          {
            field: "updated_at",
            headerName: "Updated",
            flex: 0.6,
            minWidth: 100,
          },
        ]
      : [];

    const actionsColumn = {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params);
            }}
          >
            <img
              src={deleteIcon}
              alt="delete"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    };

    return [...baseColumns, ...timestampColumns, actionsColumn];
  }, [handleTableRowClick, tableRowsData]);
  const supplierRef = useRef(null);
  const dispatch = useDispatch();

  // Fetch country data on component mount
  useEffect(() => {
    dispatch(HandleApiActions({ ...profileActions.get_coutry_regions }));
  }, [dispatch]);

  useEffect(() => {
    if (open === "11") {
      const timeout = setTimeout(() => {
        if (
          supplierRef.current &&
          supplierRef.current.scrollHeight > supplierRef.current.clientHeight
        ) {
          supplierRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (activeTab === 0) {
      setPage(1);
      setTableRowsData([]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 0) return;
    let isActive = true;
    const requestKey = `${activeTab}-${page}-${rowsPerPage}`;
    lastSupplierRequestKeyRef.current = requestKey;

    (async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_supplier_details,
          show_toast: false,
          params: {
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );

      if (!isActive || lastSupplierRequestKeyRef.current !== requestKey) {
        return;
      }

      const suppliersData =
        resp?.data?.ecu_suppliers ?? resp?.data ?? resp?.response?.data ?? [];

      const mappedData = suppliersData?.map((item, index) => {
        const baseData = {
          ...item,
          sno: (page - 1) * rowsPerPage + index + 1,
          id: item?.id || index,
          supplier_name: item?.name,
          supplier_address: item?.address,
          supplier_country: item?.country,
          allData: item,
        };
        if (item?.created_at && item?.updated_at) {
          baseData.created_at = convertUtcToTimeZone(
            item?.created_at,
            timeZoneDetails?.profile?.profile?.region,
          );
          baseData.updated_at = convertUtcToTimeZone(
            item?.updated_at,
            timeZoneDetails?.profile?.profile?.region,
          );
        }
        return baseData;
      });

      setTableRowsData(mappedData || []);
      setTotalSupplierCount(
        resp?.total_count ??
          resp?.totalItems ??
          resp?.total ??
          (Array.isArray(suppliersData) ? suppliersData.length : 0),
      );
    })();

    return () => {
      isActive = false;
    };
  }, [
    activeTab,
    dispatch,
    page,
    rowsPerPage,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  // Derive manufacturer count from export data (avoid reducer)
  // Update manufacturer count via get_mft_details when switching to Manufacturer tab
  useEffect(() => {
    const fetchManufacturerCount = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...v2AdminSettingsAPI.get_mft_details,
          show_toast: false,
        }),
      );
      const manufacturers = resp?.data ?? resp?.response?.data ?? [];
      const total = resp?.total_count ?? resp?.totalItems ?? resp?.total;
      const derivedTotal = Array.isArray(manufacturers)
        ? manufacturers.length
        : 0;
      setManufacturerCount(typeof total === "number" ? total : derivedTotal);
    };
    if (activeTab === 1) {
      fetchManufacturerCount();
    }
  }, [activeTab, rowsPerPage, dispatch]);

  const filteredRows = useMemo(() => tableRowsData, [tableRowsData]);

  // Add pagination
  // Pagination is handled by CustomDataGridV2 with totalItems

  // Add pagination handler
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // No local search state; API search resets page within callback

  // Function to handle export to Excel
  const handleExportToExcel = () => {
    if (activeTab === 0) {
      // Export Supplier Details
      if (!filteredRows || filteredRows.length === 0) {
        console.warn("No data to export");
        return;
      }

      const exportData = filteredRows?.map((row) => ({
        "S.No": row.sno,
        "Supplier Name": row.supplier_name,
        Address: row.supplier_address,
        ...(row.created_at && { Created: row.created_at }),
        ...(row.updated_at && { Updated: row.updated_at }),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Supplier Details");
      XLSX.writeFile(wb, "Supplier_Details.xlsx");
    } else {
      // Export Manufacturer Details
      if (!manufacturerExportData || manufacturerExportData.length === 0) {
        console.warn("No data to export");
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(manufacturerExportData);
      XLSX.utils.book_append_sheet(wb, ws, "Manufacturer Details");
      XLSX.writeFile(wb, "Manufacturer_Details.xlsx");
    }
  };

  const toggleDialog = (newOpen) => {
    setOpenAddMftDetails(newOpen);
  };

  const handleAddMftDetails = () => {
    toggleDialog(true);
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Supplier & Manufacturer" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Breadcrumbs and description */}
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      {/* CustomTabsV2 Interface */}
      <Box>
        <CustomTabsV2
          headingCentered={false}
          onTabChange={setActiveTab}
          tabs={[
            {
              name: "Supplier",
              count: totalSupplierCount,
              content: (
                <>
                  {/* Search Controls for Details Tab */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: {
                        xs: "0 0px 2px 0px",
                        md: "0 0px 4px 0px",
                      },
                      flexShrink: 0,
                      mt: -2.5,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box>
                        <CustomSearchbarWithApiV2
                          params={{
                            page_number: page,
                            page_size: rowsPerPage,
                          }}
                          apiAction={adminSettingsAPI.get_supplier_details}
                          searchKey={"name"}
                          getDataOnSearch={(data, resp) => {
                            const mapped = (data || [])?.map((item, index) => {
                              const baseData = {
                                ...item,
                                sno: (page - 1) * rowsPerPage + index + 1,
                                id: item?.id || index,
                                supplier_name: item?.name,
                                supplier_address: item?.address,
                                supplier_country: item?.country,
                                allData: item,
                              };
                              if (item?.created_at && item?.updated_at) {
                                baseData.created_at = convertUtcToTimeZone(
                                  item?.created_at,
                                  timeZoneDetails?.profile?.profile?.region,
                                );
                                baseData.updated_at = convertUtcToTimeZone(
                                  item?.updated_at,
                                  timeZoneDetails?.profile?.profile?.region,
                                );
                              }
                              return baseData;
                            });
                            setTableRowsData(mapped);
                            setTotalSupplierCount(
                              (resp &&
                                (resp.total_count ??
                                  resp.totalItems ??
                                  resp.total)) ??
                                (Array.isArray(data) ? data.length : 0),
                            );
                          }}
                          placeholder={"Search"}
                        />
                      </Box>
                      <Box>
                        <AddButtonV2 handleOnClick={handleAddMftDetails} />
                      </Box>
                    </Box>
                    <Box>
                      <CustomButtonV2
                        startIcon={<FileDownloadOutlinedIcon />}
                        type="submit"
                        size="small"
                        btnName="Export"
                        handleOnClick={handleExportToExcel}
                      />
                    </Box>
                  </Box>

                  {/* DataGrid Container */}
                  <Box
                    sx={{
                      height: "68vh", // ✅ Takes 72% of screen height
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CustomDataGridV2
                      showPagination={true}
                      rows={filteredRows}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      totalItems={totalSupplierCount}
                      handlePageChange={handlePageChange}
                      columns={COLUMNS}
                      isMultiLine={true}
                    />
                  </Box>
                </>
              ),
            },
            {
              name: "Manufacturer",
              count: manufacturerCount,
              content: (
                <Box>
                  <ManufacturerDetails
                    onExportDataReady={setManufacturerExportData}
                    handleExportToExcel={handleExportToExcel}
                  />
                </Box>
              ),
            },
          ]}
        />
      </Box>

      {/* Dialog components */}
      <AddSupplierDetails
        open={openAddMftDetails}
        toggleDialog={toggleDialog}
        setTotalSupplierCount={setTotalSupplierCount}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
      />

      <EditSupplierDetails
        open={openEditSupplierDetails}
        setOpenEditSupplierDetails={setOpenEditSupplierDetails}
        selectedRow={selectedRow}
      />

      <DeleteSupplierDetails
        openDeleteMftDetails={openDeleteMftDetails}
        setOpenDeleteMftDetails={setOpenDeleteMftDetails}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        setTotalSupplierCount={setTotalSupplierCount}
        setTableRowsData={setTableRowsData}
      />
    </Box>
  );
}

export default SupplierMarkets;

SupplierMarkets.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

const AddSupplierDetails = ({
  open,
  toggleDialog,
  setTotalSupplierCount,
  setPage,
  rowsPerPage,
}) => {
  const dispatch = useDispatch();
  const countryRegionsStore = useSelector(
    (store) => store.countryAndPhoneCodeReducer,
  );

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [selectedCountries, setSelectedCountries] = useState([]);

  const handleClose = () => {
    toggleDialog(false);
    setName("");
    setAddress("");
    setSelectedCountries([]);
  };

  const handleClickAdd = () => {
    const reqObj = {
      current_time: moment().format(),
      name: name,
      address: address,
      country: selectedCountries,
    };
    (async () => {
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_supplier_details,
          show_toast: true,
          params: reqObj,
        }),
      );

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_supplier_details,
          show_toast: false,
          params: {
            page_number: 1,
            page_size: rowsPerPage,
          },
        }),
      );

      setTotalSupplierCount(resp?.total_count || 0);
      setPage(1);
      // Close dialog and reset form
      toggleDialog(false);
      setName("");
      setAddress("");
      setSelectedCountries([]);
    })();
  };

  // Prepare country options from countryAndPhoneCode
  const countryOptions = (countryRegionsStore?.countryAndPhoneCode || [])
    ?.sort((a, b) => a.country.localeCompare(b.country))
    ?.map((item) => item?.country);

  return (
    <div>
      <CustomDialogBoxV2
        open={open}
        onClose={handleClose}
        title={"Add Supplier Details"}
        successButtonName={"Add"}
        rejectButtonName={"Close"}
        onClickSuccess={handleClickAdd}
        onDisabled={
          !(
            name?.length > 2 &&
            name?.trim() !== "" &&
            address?.length > 2 &&
            address?.trim() !== "" &&
            selectedCountries?.length > 0
          )
        }
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <CustomFormTextFieldV2
            label="Name"
            value={name}
            // setValue={setName}
            setValue={(val) => {
              if (val.length <= 35) {
                setName(val);
              }
            }}
            required={true}
            maxLength={35}
          />
          <CustomFormTextFieldV2
            label="Address"
            value={address}
            setValue={(val) => {
              if (val.length <= 35) {
                setAddress(val);
              }
            }}
            required={true}
            maxLength={35}
          />
          <Box>
            <Box sx={{ mb: 1 }}>
              <label
                htmlFor="supplier-country-autocomplete"
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#666",
                }}
              >
                Select Country <span style={{ color: "red" }}>*</span>
              </label>
            </Box>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={countryOptions}
              value={selectedCountries}
              onChange={(event, newValue) => {
                setSelectedCountries(newValue);
              }}
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: "8px 12px !important",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Checkbox
                    checked={selected}
                    sx={{
                      marginRight: 1,
                      color: "#000",
                      "&.Mui-checked": {
                        color: "#000",
                      },
                    }}
                  />
                  {option}
                </Box>
              )}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="supplier-country-autocomplete"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "32px",
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: "#c9c9c9ff",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#000000",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#000000",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    maxHeight: 250,
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#555",
                      },
                    },
                  },
                },
              }}
            />
            {/* Chips container with ChipsTextField styling */}
            <Box
              sx={{
                maxHeight: "95px",
                overflow: "auto",
                px: 0.5,
                pt: 0.5,
                mb: 2,
                ...(selectedCountries?.length > 0 && {
                  border: "1px solid lightgrey",
                }),
              }}
            >
              {selectedCountries?.map((country, index) => (
                <Box
                  key={country}
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    mb: 0.5,
                    mr: 0.5,
                    backgroundColor: "#232323",
                    color: "white",
                    borderRadius: 0,
                    padding: "4px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                    },
                  }}
                  onClick={() => {
                    setSelectedCountries(
                      selectedCountries.filter((c) => c !== country),
                    );
                  }}
                >
                  {country}
                  <span
                    style={{
                      marginLeft: "8px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </span>
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>
      </CustomDialogBoxV2>
    </div>
  );
};

AddSupplierDetails.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setTotalSupplierCount: PropTypes.func,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
};

const EditSupplierDetails = ({
  open,
  setOpenEditSupplierDetails,
  selectedRow,
}) => {
  const dispatch = useDispatch();

  // Form states
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  // Populate fields when `selectedRow` changes
  useEffect(() => {
    if (selectedRow) {
      setEditName(selectedRow?.name || selectedRow?.supplier_name || "");
      setEditAddress(
        selectedRow?.address || selectedRow?.supplier_address || "",
      );
    }
  }, [selectedRow]);

  // Handle dialog close
  const handleClose = () => {
    setOpenEditSupplierDetails(false);
  };

  // Handle the edit submission
  const handleClickEdit = async () => {
    // Create the request object - for suppliers we might need to delete and re-add
    // or use a different approach since there's no direct update API
    const deleteReqObj = {
      current_time: moment().format(),
      name: selectedRow?.name,
      address: selectedRow?.address,
      country: selectedRow?.country,
    };

    const addReqObj = {
      current_time: moment().format(),
      name: editName,
      address: editAddress,
    };

    // First delete the old entry
    await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.delete_supplier_details,
        show_toast: false,
        params: deleteReqObj,
      }),
    );

    // Then add the new entry
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.add_supplier_details,
        show_toast: true,
        params: addReqObj,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_admin_settings,
          show_toast: false,
        }),
      );
    });

    handleClose();
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"UPDATE SUPPLIER DETAILS"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2}>
        {/* Name field */}
        <CustomFormTextFieldV2
          label={"Name"}
          value={editName}
          setValue={setEditName}
          txtFldStyle={{
            width: "270px",
          }}
          required={true}
        />

        {/* Address field */}
        <CustomFormTextFieldV2
          label={"Address"}
          value={editAddress}
          setValue={setEditAddress}
          txtFldStyle={{
            width: "270px",
          }}
          required={true}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditSupplierDetails.propTypes = {
  open: PropTypes.bool,
  setOpenEditSupplierDetails: PropTypes.func,
  selectedRow: supplierRowShape,
};

const DeleteSupplierDetails = ({
  openDeleteMftDetails,
  setOpenDeleteMftDetails,
  selectedRow,
  setPage,
  page,
  rowsPerPage,
  setTotalSupplierCount,
  setTableRowsData,
}) => {
  const dispatch = useDispatch();
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const handleDelete = async () => {
    try {
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.delete_supplier_details,
          show_toast: true,
          params: selectedRow,
        }),
      );

      setOpenDeleteMftDetails(false);

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_supplier_details,
          show_toast: false,
          params: {
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );

      const suppliersData =
        resp?.data?.ecu_suppliers ?? resp?.data ?? resp?.response?.data ?? [];

      let mappedData = suppliersData?.map((item, index) => {
        const baseData = {
          ...item,
          sno: (page - 1) * rowsPerPage + index + 1,
          id: item?.id || index,
          supplier_name: item?.name,
          supplier_address: item?.address,
          supplier_country: item?.country,
          allData: item,
        };
        if (item?.created_at && item?.updated_at) {
          baseData.created_at = convertUtcToTimeZone(
            item?.created_at,
            timeZoneDetails?.profile?.profile?.region,
          );
          baseData.updated_at = convertUtcToTimeZone(
            item?.updated_at,
            timeZoneDetails?.profile?.profile?.region,
          );
        }
        return baseData;
      });

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(suppliersData) ? suppliersData.length : 0);

      // If current page became empty but there are items overall, step back a page
      if (
        Array.isArray(mappedData) &&
        mappedData.length === 0 &&
        page > 1 &&
        totalCount > 0
      ) {
        const prevPage = page - 1;
        const prevResp = await dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_supplier_details,
            show_toast: false,
            params: {
              page_number: prevPage,
              page_size: rowsPerPage,
            },
          }),
        );

        const prevData =
          prevResp?.data?.ecu_suppliers ??
          prevResp?.data ??
          prevResp?.response?.data ??
          [];
        mappedData = prevData?.map((item, index) => {
          const baseData = {
            ...item,
            sno: (prevPage - 1) * rowsPerPage + index + 1,
            id: item?.id || index,
            supplier_name: item?.name,
            supplier_address: item?.address,
            supplier_country: item?.country,
            allData: item,
          };
          if (item?.created_at && item?.updated_at) {
            baseData.created_at = convertUtcToTimeZone(
              item?.created_at,
              timeZoneDetails?.profile?.profile?.region,
            );
            baseData.updated_at = convertUtcToTimeZone(
              item?.updated_at,
              timeZoneDetails?.profile?.profile?.region,
            );
          }
          return baseData;
        });
        setPage(prevPage);
      }

      setTableRowsData(mappedData || []);
      setTotalSupplierCount(totalCount);
    } catch (error) {
      console.error("Error deleting supplier:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={openDeleteMftDetails}
      onClose={() => setOpenDeleteMftDetails(false)}
      title={"Delete Supplier"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteMftDetails(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeleteSupplierDetails.propTypes = {
  openDeleteMftDetails: PropTypes.bool,
  setOpenDeleteMftDetails: PropTypes.func,
  selectedRow: supplierRowShape,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  setTotalSupplierCount: PropTypes.func,
  setTableRowsData: PropTypes.func,
};
